import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Upload, FileText, CheckCircle, AlertTriangle, Loader, Link2, Plus, Trash2, Play, Eye, Filter, ArrowRight, ArrowLeft, Save, Settings, ChevronRight } from 'lucide-react';

const MONTHS = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];

const SYSTEM_FIELDS = [
  { key: 'label', label: 'Label / Etichetta', required: false },
  { key: 'isrc', label: 'ISRC', required: false },
  { key: 'artist', label: 'Artista', required: false },
  { key: 'title', label: 'Titolo Traccia', required: false },
  { key: 'platform', label: 'Piattaforma / Store', required: false },
  { key: 'country', label: 'Paese / Territory', required: false },
  { key: 'streams', label: 'Streams / Ascolti', required: false },
  { key: 'gross', label: 'Importo / Revenue', required: true },
];

export default function AdminStatementsPage() {
  const [activeTab, setActiveTab] = useState('upload');
  // Upload flow state
  const [step, setStep] = useState(1); // 1=upload, 2=map columns, 3=preview, 4=done
  const [file, setFile] = useState(null);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [uploading, setUploading] = useState(false);
  // Headers / mapping
  const [headers, setHeaders] = useState([]);
  const [sampleRows, setSampleRows] = useState([]);
  const [autoMap, setAutoMap] = useState({});
  const [columnMap, setColumnMap] = useState({});
  const [filePreviewId, setFilePreviewId] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  // Saved profiles
  const [profiles, setProfiles] = useState([]);
  const [profileName, setProfileName] = useState('');
  // Preview
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  // Generate
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  // Statements list
  const [statements, setStatements] = useState([]);
  const [stmtLoading, setStmtLoading] = useState(true);
  const [monthlyData, setMonthlyData] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [monthStatements, setMonthStatements] = useState([]);
  const [monthLoading, setMonthLoading] = useState(false);
  const [filterUser, setFilterUser] = useState('');
  // Mappings & users
  const [mappings, setMappings] = useState([]);
  const [users, setUsers] = useState([]);
  const [newMap, setNewMap] = useState({ label_name: '', user_id: '' });
  const [loadingMaps, setLoadingMaps] = useState(true);

  useEffect(() => { loadData(); loadStatements(); loadMonthly(filterYear); loadProfiles(); }, []);
  useEffect(() => { loadMonthly(filterYear); }, [filterYear]);

  const loadData = async () => {
    try {
      try { const { data } = await api.get('/statements/label-mappings'); setMappings(data.mappings || []); } catch(e){}
      try { const { data } = await api.get('/admin/users-list'); setUsers(data.users || []); } catch(e) {
        try { const { data } = await api.get('/admin/users?limit=500'); setUsers((data.users||[]).filter(u=>u.role!=='admin')); } catch(e2){}
      }
    } finally { setLoadingMaps(false); }
  };
  const loadStatements = async () => { setStmtLoading(true); try { const { data } = await api.get('/statements/admin/all'); setStatements(data.statements||[]); } catch(e){} finally { setStmtLoading(false); } };
  const loadMonthly = async (yr) => { setStmtLoading(true); try { const { data } = await api.get(`/statements/admin/monthly?year=${yr}`); setMonthlyData(data.months||[]); setAvailableYears(data.years||[]); } catch(e){} finally { setStmtLoading(false); } };
  const openMonth = async (m, y) => { setSelectedMonth({m, y}); setMonthLoading(true); try { const { data } = await api.get(`/statements/admin/all?month=${m}&year=${y}`); setMonthStatements(data.statements||[]); } catch(e){} finally { setMonthLoading(false); } };
  const loadProfiles = async () => { try { const { data } = await api.get('/statements/mapping-profiles'); setProfiles(data.profiles||[]); } catch(e){} };

  const delStatement = async (id) => { if (!window.confirm('Eliminare?')) return; try { await api.delete(`/statements/admin/${id}`); toast.success('Eliminato'); loadStatements(); } catch(e) { toast.error('Errore'); } };
  const addMapping = async () => { if (!newMap.label_name||!newMap.user_id) return toast.error('Compila tutti i campi'); try { await api.post('/statements/add-label-mapping', newMap); toast.success('Aggiunto!'); setNewMap({label_name:'',user_id:''}); loadData(); } catch(e) { toast.error('Errore'); } };
  const delMapping = async (id) => { try { await api.delete(`/statements/label-mappings/${id}`); toast.success('Rimosso'); loadData(); } catch(e){toast.error('Errore');} };

  // STEP 1: Upload file → read headers
  const uploadAndReadHeaders = async () => {
    if (!file) return toast.error('Seleziona un file');
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post('/statements/read-headers', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setHeaders(data.headers || []);
      setSampleRows(data.sampleRows || []);
      setAutoMap(data.autoMap || {});
      setColumnMap(data.autoMap || {});
      setTotalRows(data.totalRows || 0);
      setFilePreviewId(data.previewId);
      setStep(2);
      toast.success(`${data.totalRows} righe trovate — mappa le colonne`);
    } catch (err) { toast.error(err.response?.data?.error || 'Errore lettura file'); }
    finally { setUploading(false); }
  };

  // STEP 2: Map column
  const setMap = (sysField, fileCol) => { setColumnMap(prev => ({ ...prev, [sysField]: fileCol || undefined })); };

  const applyProfile = (p) => { try { const m = typeof p.mappings === 'string' ? JSON.parse(p.mappings) : p.mappings; setColumnMap(m); toast.success(`Profilo "${p.profile_name}" applicato`); } catch(e){} };
  const saveProfile = async () => {
    if (!profileName.trim()) return toast.error('Dai un nome al profilo');
    try { await api.post('/statements/mapping-profiles', { profile_name: profileName.trim(), mappings: columnMap }); toast.success('Profilo salvato!'); setProfileName(''); loadProfiles(); }
    catch(e) { toast.error('Errore'); }
  };
  const delProfile = async (id) => { try { await api.delete(`/statements/mapping-profiles/${id}`); loadProfiles(); } catch(e){} };

  // STEP 3: Preview
  const doPreview = async () => {
    if (!columnMap.gross) return toast.error('Mappa almeno la colonna Importo');
    setPreviewing(true); setPreview(null);
    try {
      const { data } = await api.post('/statements/preview-with-mapping', { previewId: filePreviewId, period_month: month, period_year: year, columnMap });
      if (data.success) { setPreview(data); setPreviewId(data.previewId || filePreviewId); setStep(3); toast.success('Anteprima pronta!'); }
      else toast.error(data.error || 'Errore');
    } catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
    finally { setPreviewing(false); }
  };

  // STEP 4: Generate
  const doGenerate = async () => {
    if (!window.confirm(`Generare ${preview?.users?.length || 0} rendiconti per ${MONTHS[month-1]} ${year}?`)) return;
    setGenerating(true);
    try {
      const { data } = await api.post('/statements/generate', { previewId: previewId || filePreviewId });
      if (data.success) { setResult(data); setStep(4); toast.success(`${data.stats?.statementsGenerated || 0} rendiconti generati!`); loadStatements(); }
      else toast.error(data.error || 'Errore');
    } catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
    finally { setGenerating(false); }
  };

  const resetUpload = () => { setStep(1); setFile(null); setHeaders([]); setSampleRows([]); setAutoMap({}); setColumnMap({}); setPreview(null); setResult(null); setFilePreviewId(null); setPreviewId(null); };

  const filteredStmts = filterUser ? statements.filter(s => String(s.user_id) === filterUser) : statements;

  const tabs = [
    { id: 'upload', label: 'Carica Rendiconto', icon: '📤' },
    { id: 'list', label: `Caricati (${statements.length})`, icon: '📊' },
    { id: 'mappings', label: 'Label Mappings', icon: '🔗' },
    { id: 'profiles', label: `Profili Colonne (${profiles.length})`, icon: '⚙️' },
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-4">Rendiconti</h1>
      <div className="flex gap-2 mb-6 flex-wrap">
        {tabs.map(t => (<button key={t.id} onClick={() => setActiveTab(t.id)} className={`px-4 py-2 rounded-lg text-sm font-medium transition ${activeTab === t.id ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' : 'bg-gray-800 text-gray-400 border border-gray-700'}`}>{t.icon} {t.label}</button>))}
      </div>

      {/* ===== TAB: UPLOAD FLOW ===== */}
      {activeTab === 'upload' && (
        <div>
          {/* Progress steps */}
          <div className="flex items-center gap-2 mb-6">
            {['Carica File', 'Mappa Colonne', 'Anteprima', 'Completato'].map((s, i) => (
              <React.Fragment key={i}>
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${step > i + 1 ? 'bg-green-900 text-green-300' : step === i + 1 ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' : 'bg-gray-800 text-gray-500'}`}>
                  {step > i + 1 ? <CheckCircle size={12} /> : <span className="w-4 h-4 rounded-full border flex items-center justify-center text-[10px]">{i+1}</span>}{s}
                </div>
                {i < 3 && <ArrowRight size={14} className="text-gray-600" />}
              </React.Fragment>
            ))}
          </div>

          {/* STEP 1: Upload */}
          {step === 1 && (
            <div className="card-dark p-6">
              <h2 className="font-bold text-white mb-4">1. Carica il file master</h2>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div><label className="block text-xs text-gray-400 mb-1">Mese</label><select className="input-field-dark" value={month} onChange={e => setMonth(parseInt(e.target.value))}>{MONTHS.map((m,i) => <option key={i} value={i+1}>{m}</option>)}</select></div>
                <div><label className="block text-xs text-gray-400 mb-1">Anno</label><input type="number" className="input-field-dark" value={year} onChange={e => setYear(parseInt(e.target.value))} min={2020} max={2030} /></div>
              
              </div>
              <div className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center cursor-pointer hover:border-yellow-500/50 transition mb-4" onClick={() => document.getElementById('master-file').click()}>
                <input type="file" accept=".csv,.xlsx,.xls" onChange={e => setFile(e.target.files[0])} className="hidden" id="master-file" />
                {file ? <div className="flex items-center justify-center gap-2 text-yellow-400"><CheckCircle size={20} /><span className="text-sm">{file.name}</span></div> : <div><Upload size={32} className="mx-auto text-gray-500 mb-2" /><p className="text-sm text-gray-400">Trascina o clicca per caricare CSV / XLSX</p></div>}
              </div>
              <button onClick={uploadAndReadHeaders} disabled={uploading || !file} className="btn-admin w-full py-3 text-base">
                {uploading ? <><Loader size={16} className="animate-spin inline mr-2" />Lettura file...</> : <><ArrowRight size={16} className="inline mr-2" />Leggi Colonne</>}
              </button>
            </div>
          )}

          {/* STEP 2: Map columns */}
          {step === 2 && (
            <div className="card-dark p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-white">2. Mappa le colonne ({totalRows} righe)</h2>
                <button onClick={() => setStep(1)} className="text-xs text-gray-400 hover:text-white flex items-center gap-1"><ArrowLeft size={12} />Indietro</button>
              </div>

              {/* Saved profiles */}
              <div className="bg-gray-900 rounded-lg p-3 mb-4">
                <p className="text-[10px] text-gray-400 uppercase font-bold mb-2">📁 Profili Mappatura Salvati</p>
                {profiles.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {profiles.map(p => (
                      <div key={p.id} className="flex items-center gap-1 bg-gray-800 rounded-lg overflow-hidden">
                        <button onClick={() => applyProfile(p)} className="text-xs text-yellow-400 px-3 py-1.5 hover:bg-yellow-500/20 font-medium">{p.profile_name}</button>
                        <button onClick={() => { if(window.confirm('Eliminare profilo?')) delProfile(p.id); }} className="px-2 py-1.5 text-gray-600 hover:text-red-400 hover:bg-red-900/20 border-l border-gray-700"><Trash2 size={10} /></button>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-gray-500">Nessun profilo salvato. Mappa le colonne e salva un profilo per riusarlo.</p>}
              </div>

              {/* Column mapping */}
              <div className="space-y-3 mb-4">
                {SYSTEM_FIELDS.map(sf => (
                  <div key={sf.key} className="flex items-center gap-3">
                    <span className={`text-sm w-40 flex-shrink-0 ${sf.required ? 'text-yellow-400 font-bold' : 'text-gray-300'}`}>{sf.label}{sf.required ? ' ⚠️' : ''}</span>
                    <select className="input-field-dark flex-1" value={columnMap[sf.key] || ''} onChange={e => setMap(sf.key, e.target.value)}>
                      <option value="">— Non mappata —</option>
                      {headers.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                    {columnMap[sf.key] && autoMap[sf.key] === columnMap[sf.key] && <span className="text-[10px] text-green-400">auto</span>}
                  </div>
                ))}
              </div>

              {/* Sample data */}
              {sampleRows.length > 0 && (
                <div className="bg-gray-900 rounded-lg p-3 mb-4 overflow-x-auto">
                  <p className="text-[10px] text-gray-400 uppercase font-bold mb-2">Anteprima dati (prime {sampleRows.length} righe)</p>
                  <table className="text-[10px] text-gray-300 w-full">
                    <thead><tr>{headers.map(h => <th key={h} className="text-left px-2 py-1 text-gray-500 font-medium whitespace-nowrap">{h}</th>)}</tr></thead>
                    <tbody>{sampleRows.map((r, i) => <tr key={i} className="border-t border-gray-800">{headers.map(h => <td key={h} className="px-2 py-1 whitespace-nowrap">{String(r[h] || '').substring(0, 40)}</td>)}</tr>)}</tbody>
                  </table>
                </div>
              )}

              {/* Save profile */}
              <div className="bg-gray-900 rounded-lg p-3 mb-4">
                <p className="text-[10px] text-gray-400 uppercase font-bold mb-2">💾 Salva Mappatura</p>
                <div className="flex items-center gap-2">
                  <input className="input-field-dark flex-1 text-xs" placeholder="es. DistroKid, TuneCore, iMusician..." value={profileName} onChange={e => setProfileName(e.target.value)} />
                  <button onClick={saveProfile} disabled={!profileName.trim()} className="btn-admin text-xs px-4 py-2.5"><Save size={12} className="inline mr-1" />Salva Profilo</button>
                </div>
              </div>

              <button onClick={doPreview} disabled={previewing || !columnMap.gross} className="btn-admin w-full py-3 text-base">
                {previewing ? <><Loader size={16} className="animate-spin inline mr-2" />Calcolo...</> : <><Eye size={16} className="inline mr-2" />Genera Anteprima</>}
              </button>
            </div>
          )}

          {/* STEP 3: Preview */}
          {step === 3 && preview && (
            <div className="card-dark p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-white">3. Anteprima — {MONTHS[month-1]} {year}</h2>
                <button onClick={() => setStep(2)} className="text-xs text-gray-400 hover:text-white flex items-center gap-1"><ArrowLeft size={12} />Modifica mappatura</button>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-gray-900 rounded-lg p-4 text-center"><p className="text-2xl font-bold text-white">{preview.stats?.totalRows}</p><p className="text-[10px] text-gray-500">Righe file</p></div>
                <div className="bg-gray-900 rounded-lg p-4 text-center"><p className="text-2xl font-bold text-green-400">{preview.stats?.usersMatched}</p><p className="text-[10px] text-gray-500">Utenti trovati</p></div>
                <div className="bg-gray-900 rounded-lg p-4 text-center"><p className="text-2xl font-bold text-orange-400">{preview.stats?.unmappedCount}</p><p className="text-[10px] text-gray-500">Label non mappate</p></div>
              </div>
              {preview.users?.map((u, i) => (
                <div key={i} className="bg-gray-900 rounded-lg p-4 flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-yellow-500/20 text-yellow-400 flex items-center justify-center font-bold text-sm">{u.name?.[0]}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-medium">{u.name}</p>
                    <p className="text-[10px] text-gray-500">{u.email} · {u.totalLines} righe · {u.totalStreams?.toLocaleString()} streams · PN {u.commission}%</p>
                  </div>
                  <div className="text-right"><p className="text-lg font-bold text-green-400">€{u.totalUserShare?.toFixed(2)}</p><p className="text-[10px] text-gray-500">PN: €{u.totalPnShare?.toFixed(2)}</p></div>
                </div>
              ))}
              {/* Calculation breakdown */}
              {preview.calculationSample && (
                <div className="bg-gray-900 rounded-lg p-4 mb-3">
                  <p className="text-xs text-gray-400 font-bold uppercase mb-2">Esempio calcolo (prima riga)</p>
                  <div className="grid grid-cols-5 gap-2 text-xs text-center">
                    <div className="bg-gray-800 rounded p-2"><p className="text-gray-500">Importo</p><p className="text-white font-bold">€{preview.calculationSample.grossAmount?.toFixed(6)}</p></div>
                    <div className="bg-gray-800 rounded p-2"><p className="text-gray-500">- Fee {preview.distFeePercent}%</p><p className="text-red-400 font-bold">€{preview.calculationSample.distFee?.toFixed(6)}</p></div>
                    <div className="bg-gray-800 rounded p-2"><p className="text-gray-500">Netto</p><p className="text-blue-400 font-bold">€{preview.calculationSample.net?.toFixed(6)}</p></div>
                    <div className="bg-gray-800 rounded p-2"><p className="text-gray-500">PN {preview.calculationSample.pnCommission}%</p><p className="text-orange-400 font-bold">€{preview.calculationSample.pnShare?.toFixed(6)}</p></div>
                    <div className="bg-gray-800 rounded p-2"><p className="text-gray-500">Utente</p><p className="text-green-400 font-bold">€{preview.calculationSample.userShare?.toFixed(6)}</p></div>
                  </div>
                </div>
              )}
              {preview.grandTotals && (
                <div className="bg-yellow-900/20 border border-yellow-800 rounded-lg p-4 mb-3 flex items-center justify-between">
                  <div><p className="text-xs text-gray-400">Totale Lordo</p><p className="text-white font-bold">€{preview.grandTotals.gross?.toFixed(2)}</p></div>
                  <div className="text-2xl text-gray-600">→</div>
                  <div><p className="text-xs text-gray-400">Totale PN</p><p className="text-orange-400 font-bold">€{preview.grandTotals.pnShare?.toFixed(2)}</p></div>
                  <div className="text-2xl text-gray-600">→</div>
                  <div><p className="text-xs text-gray-400">Totale Utenti</p><p className="text-green-400 font-bold text-xl">€{preview.grandTotals.userShare?.toFixed(2)}</p></div>
                </div>
              )}
              {preview.unmapped_labels?.length > 0 && (
                <div className="p-3 bg-orange-900/20 border border-orange-800 rounded-lg my-3">
                  <p className="text-xs text-orange-400 mb-1"><AlertTriangle size={12} className="inline mr-1" />Label non mappate ({preview.unmapped_labels.length}):</p>
                  <div className="flex flex-wrap gap-1">{preview.unmapped_labels.map((l, i) => <button key={i} onClick={() => { setNewMap(p => ({...p, label_name: l})); setActiveTab('mappings'); }} className="text-xs bg-orange-900/30 text-orange-300 px-2 py-1 rounded hover:bg-orange-800">{l}</button>)}</div>
                </div>
              )}
              <button onClick={doGenerate} disabled={generating} className="w-full py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 flex items-center justify-center gap-2 mt-4">
                {generating ? <><Loader size={16} className="animate-spin" />Generazione...</> : <><Play size={16} />Conferma e Genera Rendiconti</>}
              </button>
            </div>
          )}

          {/* STEP 4: Done */}
          {step === 4 && result && (
            <div className="card-dark p-6 border border-green-500/30">
              <h2 className="font-bold text-green-400 text-xl mb-4">✅ Rendiconti generati!</h2>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-gray-900 rounded-lg p-4 text-center"><p className="text-2xl font-bold text-white">{result.stats?.totalRows}</p><p className="text-[10px] text-gray-500">Righe</p></div>
                <div className="bg-gray-900 rounded-lg p-4 text-center"><p className="text-2xl font-bold text-white">{result.stats?.usersMatched}</p><p className="text-[10px] text-gray-500">Utenti</p></div>
                <div className="bg-gray-900 rounded-lg p-4 text-center"><p className="text-2xl font-bold text-green-400">{result.stats?.statementsGenerated}</p><p className="text-[10px] text-gray-500">Rendiconti</p></div>
              </div>
              {result.errors?.length > 0 && <div className="p-3 bg-red-900/20 rounded-lg mb-3 text-xs text-red-400">{result.errors.map((e,i) => <p key={i}>{e}</p>)}</div>}
              <p className="text-sm text-gray-400 mb-4">PDF generati e email inviate agli utenti.</p>
              <button onClick={resetUpload} className="btn-admin w-full py-3"><Plus size={16} className="inline mr-2" />Carica Altro</button>
            </div>
          )}
        </div>
      )}

      {/* ===== TAB: STATEMENTS LIST ===== */}
      {activeTab === 'list' && (
        <div className="card-dark p-6">
          {selectedMonth ? (<>
            <div className="flex items-center gap-4 mb-4">
              <button onClick={() => { setSelectedMonth(null); setMonthStatements([]); }} className="text-gray-400 hover:text-white text-sm flex items-center gap-1"><ArrowLeft size={16} />Indietro</button>
              <h2 className="font-bold text-white flex-1">{MONTHS[selectedMonth.m-1]} {selectedMonth.y} ({monthStatements.length} rendiconti)</h2>
            </div>
            {monthLoading ? <Loader size={20} className="animate-spin text-yellow-400 mx-auto" /> : monthStatements.length === 0 ? <p className="text-center text-gray-500 py-8">Nessun rendiconto</p> : (
              <div className="space-y-2">{monthStatements.map(s => (
                <div key={s.id} className="bg-gray-900 rounded-lg p-4 flex items-center gap-4">
                  <FileText size={18} className="text-yellow-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0"><p className="text-white font-medium">{s.first_name} {s.last_name}</p><p className="text-xs text-gray-400">{s.email}</p></div>
                  <div className="text-right flex-shrink-0"><p className="text-white font-bold">€{parseFloat(s.user_share).toFixed(2)}</p><p className="text-[10px] text-gray-500">PN: €{parseFloat(s.proudnoize_share).toFixed(2)}</p></div>
                  <span className={`text-[10px] px-2 py-1 rounded-full ${s.status==='generated'?'bg-green-900 text-green-300':'bg-gray-700 text-gray-300'}`}>{s.status}</span>
                  <button onClick={() => delStatement(s.id)} className="p-1.5 text-gray-600 hover:text-red-500"><Trash2 size={14} /></button>
                </div>
              ))}</div>
            )}
          </>) : (<>
            <div className="flex items-center gap-4 mb-4">
              <h2 className="font-bold text-white flex-1">Rendiconti per Mese</h2>
              <select className="input-field-dark w-32" value={filterYear} onChange={e => setFilterYear(parseInt(e.target.value))}>
                {(availableYears.length ? availableYears : [new Date().getFullYear()]).map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            {stmtLoading ? <Loader size={20} className="animate-spin text-yellow-400 mx-auto" /> : monthlyData.length === 0 ? <p className="text-center text-gray-500 py-8">Nessun rendiconto per il {filterYear}</p> : (
              <div className="space-y-2">{monthlyData.map(m => (
                <div key={`${m.period_month}-${m.period_year}`} onClick={() => openMonth(m.period_month, m.period_year)} className="bg-gray-900 rounded-lg p-4 flex items-center gap-4 cursor-pointer hover:bg-gray-800 transition">
                  <div className="w-12 h-12 rounded-xl bg-yellow-900/30 text-yellow-400 flex items-center justify-center font-bold text-sm flex-shrink-0">{String(m.period_month).padStart(2, '0')}</div>
                  <div className="flex-1"><p className="text-white font-bold">{MONTHS[m.period_month-1]} {m.period_year}</p><p className="text-[10px] text-gray-500">{m.count} rendiconti</p></div>
                  <div className="text-right"><p className="text-[10px] text-orange-500">PN</p><p className="text-orange-400 font-bold">€{parseFloat(m.pn_share).toFixed(2)}</p></div>
                  <div className="text-right"><p className="text-[10px] text-green-500">Label</p><p className="text-green-400 font-bold">€{parseFloat(m.user_share).toFixed(2)}</p></div>
                  <ChevronRight size={16} className="text-gray-600" />
                </div>
              ))}</div>
            )}
          </>)}
        </div>
      )}

      {/* ===== TAB: PROFILES ===== */}
      {activeTab === 'profiles' && (
        <div className="card-dark p-6">
          <h2 className="font-bold text-white mb-4"><Settings size={18} className="inline mr-2 text-yellow-400" />Profili Mappatura Colonne</h2>
          <p className="text-xs text-gray-500 mb-4">Ogni profilo salva la mappatura tra le colonne del tuo file e i campi del sistema. Quando carichi un nuovo file, puoi applicare un profilo per compilare automaticamente la mappatura.</p>
          {profiles.length === 0 ? (
            <div className="bg-gray-900 rounded-lg p-8 text-center"><p className="text-gray-500 text-sm">Nessun profilo salvato</p><p className="text-xs text-gray-600 mt-2">Per salvare un profilo: Carica Rendiconto → Step 2 (Mappa Colonne) → Salva Profilo</p></div>
          ) : (
            <div className="space-y-3">
              {profiles.map(p => {
                let mappings = {}; try { mappings = typeof p.mappings === 'string' ? JSON.parse(p.mappings) : (p.mappings || {}); } catch(e) {}
                return (
                  <div key={p.id} className="bg-gray-900 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-yellow-400">{p.profile_name}</h3>
                      <div className="flex gap-2">
                        <button onClick={() => { if(window.confirm('Eliminare questo profilo?')) delProfile(p.id); }} className="text-xs text-red-400 hover:underline flex items-center gap-1"><Trash2 size={12} />Elimina</button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {Object.entries(mappings).filter(([k,v]) => v).map(([key, val]) => (
                        <div key={key} className="bg-gray-800 rounded p-2 text-xs">
                          <span className="text-gray-500">{SYSTEM_FIELDS.find(f => f.key === key)?.label || key}:</span>
                          <span className="text-white ml-1 font-medium">{val}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-gray-600 mt-2">Creato: {new Date(p.created_at).toLocaleDateString('it-IT')}{p.updated_at ? ` · Aggiornato: ${new Date(p.updated_at).toLocaleDateString('it-IT')}` : ''}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

            {/* ===== TAB: LABEL MAPPINGS ===== */}
      {activeTab === 'mappings' && (
        <div className="card-dark p-6">
          <h2 className="font-bold text-white mb-2"><Link2 size={18} className="inline mr-2 text-yellow-400" />Label Mappings ({mappings.length})</h2>
          <p className="text-xs text-gray-500 mb-4">Collega il nome della label nel file master all'utente della workstation. Puoi mappare le label prima di caricare il file — verranno usate automaticamente.</p>
          <div className="bg-gray-900 rounded-lg p-4 mb-4">
            <div className="space-y-2">
              <input className="input-field-dark" placeholder="Nome label nel file..." value={newMap.label_name} onChange={e => setNewMap(p => ({...p, label_name: e.target.value}))} />
              <select className="input-field-dark" value={newMap.user_id} onChange={e => setNewMap(p => ({...p, user_id: e.target.value}))}><option value="">Seleziona utente...</option>{users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</option>)}</select>
              <button onClick={addMapping} className="btn-admin w-full"><Plus size={14} className="inline mr-1" />Aggiungi</button>
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-[10px] text-gray-400 uppercase font-bold mb-1">Mappature attive</p>
            {mappings.length === 0 ? <p className="text-sm text-gray-500 text-center py-6 bg-gray-900 rounded-lg">Nessuna mappatura. Aggiungi la prima qui sopra.</p> : mappings.map(m => (
              <div key={m.id} className="flex items-center gap-3 p-3 bg-gray-900 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-yellow-500/20 text-yellow-400 flex items-center justify-center flex-shrink-0 text-xs font-bold">{(m.first_name||'?')[0]}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">"{m.master_file_label_name}"</p>
                  <p className="text-[10px] text-gray-400">→ {m.first_name} {m.last_name}{m.email ? ` (${m.email})` : ''}</p>
                </div>
                <span className="text-[10px] bg-green-900 text-green-300 px-2 py-0.5 rounded-full">attivo</span>
                <button onClick={() => delMapping(m.id)} className="p-1.5 text-gray-600 hover:text-red-500"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
